/** The wire form: plain JSON, with the typed arrays tagged exactly the way
 *  `hash/canonical.ts` tags them.
 *
 * Writing is `canonicalize`, unchanged: the bytes of a save are the bytes the
 * hash is taken over, so "the same game saved twice is byte-for-byte identical"
 * is a property of the encoder rather than a thing to hope for.
 *
 * Reading is the part that needs care. Everything below `decodeTree` is
 * untrusted input - a hand-edited file, a file from a newer build, a file from
 * a different game - so it is validated and *returned* as an `Err`, never
 * thrown (Section 3.2).
 */

import type { PersistError } from "./types.js";
import { malformedFile } from "./types.js";
import type { Result } from "../result.js";
import { Err, Ok } from "../result.js";
import {
  TA_TAG_KEY, TA_VALUES_KEY, TYPED_ARRAYS, canonicalize, typedArrayTag,
} from "../hash/canonical.js";

export type Json =
  | null | boolean | number | string
  | readonly Json[]
  | { readonly [k: string]: Json };

/** A hand-edited file can nest arbitrarily; recursion is not a crash report. */
const MAX_DEPTH = 64;

/** Inclusive value range per tag, so `[300]` never silently becomes `[44]`. */
const RANGES: Readonly<Record<string, readonly [number, number]>> = {
  i8: [-128, 127],
  u8: [0, 255],
  u8c: [0, 255],
  i16: [-32768, 32767],
  u16: [0, 65535],
  i32: [-2147483648, 2147483647],
  u32: [0, 4294967295],
};

function ctorFor(tag: string): ((values: readonly number[]) => ArrayBufferView) | null {
  for (const [t, ctor] of TYPED_ARRAYS) {
    if (t === tag) return (values) => new ctor(values);
  }
  return null;
}

// ------------------------------------------------------------------ encode

/**
 * State to JSON-safe data. The inverse of `decodeTree`, and the shape a host
 * hands to `structuredClone` or `JSON.stringify`.
 *
 * It throws rather than returning an `Err`, and deliberately: its input is our
 * own `GameState`, so a `Map`, a function or a float here is a bug in the rules
 * (I6/D4), not a bad file. `decodeTree` is the one that faces the world.
 */
export function encodeTree(v: unknown): Json {
  if (v === null) return null;
  const t = typeof v;
  if (t === "number") {
    if (!Number.isInteger(v as number)) throw new Error(`float in persisted state (D4): ${String(v)}`);
    return v as number;
  }
  if (t === "boolean" || t === "string") return v as boolean | string;
  if (t === "undefined") throw new Error("undefined in persisted state (I6): use null");
  if (t !== "object") throw new Error(`non-plain value in persisted state (I6): ${t}`);

  if (Array.isArray(v)) return (v as readonly unknown[]).map(encodeTree);

  const tag = typedArrayTag(v as object);
  if (tag !== null) {
    const a = v as unknown as ArrayLike<number>;
    const out: number[] = new Array<number>(a.length);
    for (let i = 0; i < a.length; i++) out[i] = a[i] as number;
    return { [TA_TAG_KEY]: tag, [TA_VALUES_KEY]: out };
  }
  if (v instanceof Map || v instanceof Set || v instanceof Date) {
    throw new Error("Map/Set/Date are not plain data (I6)");
  }

  const src = v as Record<string, unknown>;
  const out: Record<string, Json> = {};
  for (const k of Object.keys(src).sort()) {
    const val = src[k];
    if (val === undefined) throw new Error(`undefined value at key "${k}" (I6)`);
    out[k] = encodeTree(val);
  }
  return out;
}

// ------------------------------------------------------------------ decode

interface Tagged { readonly [TA_TAG_KEY]: unknown; readonly [TA_VALUES_KEY]: unknown }

function isTagged(o: Record<string, unknown>): boolean {
  return Object.prototype.hasOwnProperty.call(o, TA_TAG_KEY);
}

function decodeTyped(o: Tagged, path: string): Result<ArrayBufferView, PersistError> {
  const tag = o[TA_TAG_KEY];
  if (typeof tag !== "string") return Err(malformedFile(`${path}: typed-array tag is not a string`));
  const make = ctorFor(tag);
  const range = RANGES[tag];
  if (make === null || range === undefined) {
    return Err(malformedFile(`${path}: unknown typed-array tag "${tag}"`));
  }
  const values = o[TA_VALUES_KEY];
  if (!Array.isArray(values)) return Err(malformedFile(`${path}: typed array has no value list`));
  const nums: number[] = new Array<number>(values.length);
  const [lo, hi] = range;
  for (let i = 0; i < values.length; i++) {
    const n: unknown = values[i];
    if (typeof n !== "number" || !Number.isInteger(n)) {
      return Err(malformedFile(`${path}[${String(i)}]: not an integer`));
    }
    if (n < lo || n > hi) {
      return Err(malformedFile(`${path}[${String(i)}]: ${String(n)} is out of range for ${tag}`));
    }
    nums[i] = n;
  }
  const extra = Object.keys(o as unknown as Record<string, unknown>).sort()
    .filter((k) => k !== TA_TAG_KEY && k !== TA_VALUES_KEY);
  if (extra.length > 0) {
    return Err(malformedFile(`${path}: unexpected keys on a typed array: ${extra.join(", ")}`));
  }
  return Ok(make(nums));
}

/**
 * JSON back to state-shaped data: tagged nodes become typed arrays, and
 * anything that could not have come out of a kernel - a float, a non-finite
 * number - is refused here rather than three systems downstream.
 */
export function decodeTree(v: unknown, path = "$", depth = 0): Result<unknown, PersistError> {
  if (depth > MAX_DEPTH) return Err(malformedFile(`${path}: nested deeper than ${String(MAX_DEPTH)}`));
  if (v === null) return Ok(null);
  const t = typeof v;
  if (t === "number") {
    const n = v as number;
    if (!Number.isFinite(n)) return Err(malformedFile(`${path}: non-finite number`));
    if (!Number.isInteger(n)) return Err(malformedFile(`${path}: float in a persisted shape (D4)`));
    return Ok(n);
  }
  if (t === "boolean" || t === "string") return Ok(v);
  if (t !== "object") return Err(malformedFile(`${path}: ${t} is not plain data`));

  if (Array.isArray(v)) {
    const src = v as readonly unknown[];
    const out: unknown[] = new Array<unknown>(src.length);
    for (let i = 0; i < src.length; i++) {
      const r = decodeTree(src[i], `${path}[${String(i)}]`, depth + 1);
      if (!r.ok) return r;
      out[i] = r.value;
    }
    return Ok(out);
  }

  const o = v as Record<string, unknown>;
  if (isTagged(o)) return decodeTyped(o as unknown as Tagged, path);

  const out: Record<string, unknown> = {};
  for (const k of Object.keys(o).sort()) {
    const child = o[k];
    if (child === undefined) return Err(malformedFile(`${path}.${k}: undefined (I6)`));
    const r = decodeTree(child, `${path}.${k}`, depth + 1);
    if (!r.ok) return r;
    out[k] = r.value;
  }
  return Ok(out);
}

// ------------------------------------------------------------------- text

/** Canonical JSON. Sorted keys, integers, no whitespace - so two saves of the
 *  same state are the same bytes and diff cleanly in review. */
export function serialize(v: unknown): string {
  return canonicalize(v);
}

/** JSON text to a raw tree. Still untrusted; nothing is decoded yet. */
export function parseJson(text: string): Result<unknown, PersistError> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e: unknown) {
    return Err(malformedFile(`not JSON: ${e instanceof Error ? e.message : String(e)}`));
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return Err(malformedFile("the root of a save is an object"));
  }
  return Ok(parsed);
}
